import { Helmet } from "react-helmet-async";

import { BUSINESS_SAME_AS } from "@/const";
import { CANONICAL_ROUTES, SITE_BASE_URL } from "@/lib/utils";

const canonicalisePath = (path: string): string => {
  if (path === "/") {
    return path;
  }

  return path.endsWith("/") ? path : `${path}/`;
};

export function LocalBusinessSchema() {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Aura Counselling",
    "description": "Counselling in italiano ovunque online e in presenza a Zurigo con sessione introduttiva gratuita ",
    "image": `${SITE_BASE_URL}/aura-logo-no-stripes.png`,
    "url": SITE_BASE_URL,
    "telephone": "+41 (da richiedere al counsellor)",
    "email": "info@aura-counselling.com",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Zurigo",
      "addressLocality": "Zurigo",
      "postalCode": "8049",
      "addressCountry": "CH"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "areaServed": [
      {
        "@type": "City",
        "name": "Zurigo"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Cantone Zurigo"
      },
      {
        "@type": "Country",
        "name": "Svizzera"
      },
      {
        "@type": "Country",
        "name": "Italia"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Italia"
      },
      {
        "@type": "Place",
        "name": "Cantone Ticino"
      },
      {
        "@type": "Continent",
        "name": "Europa"
      },
      {
        "@type": "Place",
        "name": "Clienti italiani nel mondo"
      }
    ],
    "availableService": [
      {
        "@type": "Service",
        "name": "Counselling online in italiano",
        "description": "Sessioni di counselling online in lingua italiana per persone che vivono in Italia, in Svizzera e all'estero, con supporto flessibile via videochiamata.",
        "areaServed": [
          {
            "@type": "Country",
            "name": "Italia"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Cantone Ticino"
          },
          {
            "@type": "Continent",
            "name": "Europa"
          },
          {
            "@type": "Place",
            "name": "Italiani nel mondo"
          }
        ],
        "provider": {
          "@type": "Organization",
          "name": "Aura Counselling"
        }
      },
      {
        "@type": "Service",
        "name": "Counselling in presenza a Zurigo",
        "description": "Percorsi di counselling individuali in italiano presso lo studio di Aura Counselling a Zurigo, con accoglienza dedicata sul territorio.",
        "areaServed": [
          {
            "@type": "City",
            "name": "Zurigo"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Cantone Zurigo"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Cantone Ticino"
          }
        ],
        "provider": {
          "@type": "Organization",
          "name": "Aura Counselling"
        }
      }
    ],
    "serviceType": [
      "Counselling",
      "Counselling Online in Italiano",
      "Counselling Online in Italiano ovunque nel mondo",
      "Conselling in Presenza a Zurigo",
      "Counselling Gratuito Online",
      "Counselling a Zurigo",
      "Counselling in Ticino",
      "Counselling in Svizzera",
      "Sostegno Psicologico Online",
      "Crescita Personale"
    ]
  };

  if (BUSINESS_SAME_AS.length > 0) {
    schema["sameAs"] = BUSINESS_SAME_AS;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

export function BlogPostingSchema({
  title,
  description,
  datePublished,
  dateModified,
  url,
  author = "Aura Counselling",
  keywords
}: {
  title: string;
  description: string;
  datePublished: string;
  dateModified: string;
  url: string;
  author?: string;
  keywords?: string[];
}) {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": description,
    "image": `${SITE_BASE_URL}/aura-logo-no-stripes.png`,
    "datePublished": datePublished,
    "dateModified": dateModified,
    "inLanguage": "it-IT",
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Aura Counselling",
      "url": SITE_BASE_URL
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  };

  if (keywords && keywords.length > 0) {
    // Google expects a comma-separated list for the keywords field
    schema["keywords"] = keywords.join(", ");
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Service Schema - Servizi Offerti
 * Permette a Google di mostrare i servizi nei risultati di ricerca
 */
export function ServiceSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "Service",
                "name": "Consulenza Online",
                "description": "Sessioni di counselling online in italiano con primo incontro gratuito per persone di lingua italiana ovunque nel mondo",
            "areaServed": ["CH", "IT", "EU"],
            "price": "70",
            "priceCurrency": "CHF",
            "duration": "PT60M",
            "provider": {
              "@type": "Organization",
              "name": "Aura Counselling"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "Service",
            "name": "Consulenza in Presenza",
                "description": "Sessioni di counselling psicologico in italiano a Zurigo con percorsi personalizzati",
            "areaServed": "CH",
            "price": "90",
            "priceCurrency": "CHF",
            "duration": "PT60M",
            "provider": {
              "@type": "Organization",
              "name": "Aura Counselling"
            }
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "Service",
            "name": "Sostegno Psicologico Online",
                "description": "Sessioni di counselling online in italiano con primo incontro gratuito per persone di lingua italiana ovunque nel mondo",
            "areaServed": ["CH", "IT", "EU"],
            "duration": "PT60M",
            "provider": {
              "@type": "Organization",
              "name": "Aura Counselling"
            }
          }
        }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * Person Schema - Profilo Terapeuta
 * Aiuta Google a comprendere chi offre i servizi
 */
export function PersonSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Laura Ceschetti",
    "jobTitle": "Counselor Psicologico",
    "description": "Counselor specializzata in psicologia del benessere e crescita personale",
    "url": `${SITE_BASE_URL}${CANONICAL_ROUTES.chiSono}`,
    "image": `${SITE_BASE_URL}/aura-logo-no-stripes.png`,
    "worksFor": {
      "@type": "Organization",
      "name": "Aura Counselling",
      "url": SITE_BASE_URL
    },
    "knowsAbout": [
      "Psicologia del benessere",
      "Counseling",
      "Counselling",
      "Crescita personale",
      "Counselling online in italiano",
      "Counselling gratuito",
      "Counselling a Zurigo",
      "Counselling in Ticino",
      "Counselling in Svizzera",
      "Counselling ovunque nel mondo",
      "Counselling per persone di lingua italiana",
      "Sostegno psicologico online"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}

/**
 * BreadcrumbList Schema - Breadcrumb Navigazione
 * Migliora la visualizzazione nei risultati di ricerca
 */
export function BreadcrumbSchema({
  items
}: {
  items: Array<{ name: string; path: string }>;
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_BASE_URL}${canonicalisePath(item.path)}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
